import { upperCase } from 'lodash';
import { pathOr, path } from 'ramda';
import { combineReducers } from 'redux';

import Http from '../../http';
import HttpV2 from '../../httpv2';
import { store } from '../../../main';
import { resetOnChangeShop } from '../../../reducer';

const createConstants = namespace => ({
  fetchPage: `FETCH_${upperCase(namespace)}_PAGE_START`,
  fetchPageSuccess: `FETCH_${upperCase(namespace)}_PAGE_SUCCESS`,
  fetchPageError: `FETCH_${upperCase(namespace)}_PAGE_ERROR`,
  updateFetchConfig: `UPDATE_${upperCase(namespace)}_FETCH_CONFIG`,
});

const paginationReducerName = 'paginatedLists';

const createSelectors = (namespace) => ({
  getActivePageItems: pathOr([], [paginationReducerName, namespace, 'activePageItems']),
  getActivePageInfo: path([paginationReducerName, namespace, 'activePageInfo']),
  getBusyState: path([paginationReducerName, namespace, 'busy']),
  getFetchConfig: path([paginationReducerName, namespace, 'fetchConfig']),
});

const createActions = (constants, config, selectors) => {
  const RequestExecutor = config.httpVersion === 1 ? Http : HttpV2;

  const actionCreators = {
    fetchPage: () => ({
      type: constants.fetchPage,
    }),
    fetchPageSuccess: (payload) => ({
      type: constants.fetchPageSuccess,
      payload,
    }),
    fetchPageError: (error) => ({
      type: constants.fetchPageError,
      payload: error,
    }),
    updateFetchConfig: (newConfig) => ({
      type: constants.updateFetchConfig,
      payload: newConfig,
    }),
  };

  const fetchPage = (page) => async (dispatch) => {
    dispatch(actionCreators.fetchPage());

    const { filters = {}, url = null } = selectors.getFetchConfig(store.getState());
    const filter = {
      ...filters,
      page,
      limit: filters.limit || config.limit || 20,
    };

    try {
      const requestUrl = url || config.buildUrl(store.getState());
      const response = await RequestExecutor.get(requestUrl, filter);
      dispatch(actionCreators.fetchPageSuccess(response));
    } catch (error) {
      dispatch(actionCreators.fetchPageError(error));
    }
  };

  return { fetchPage, updateFetchConfig: actionCreators.updateFetchConfig };
};

const createReducer = (constants) => {
  const activePageItems = (state = [], action) => {
    switch (action.type) {
      case constants.fetchPageSuccess:
        return action.payload.list;
      default:
        return state;
    }
  };

  const activePageInfo = (state = null, action) => {
    switch (action.type) {
      case constants.fetchPageSuccess:
        return action.payload.pagination;
      default:
        return state;
    }
  };

  const busy = (state = false, action) => {
    switch (action.type) {
      case constants.fetchPage:
        return true;
      case constants.fetchPageSuccess:
      case constants.fetchPageError:
        return false;
      default:
        return state;
    }
  };

  const fetchConfig = (state = {}, action) => {
    switch (action.type) {
      case constants.updateFetchConfig:
        return action.payload;
      default:
        return state;
    }
  };

  return combineReducers({
    activePageItems,
    activePageInfo,
    busy,
    fetchConfig,
  });
};

const paginationReducers = {};

const createPaginatedReduxStore = (config) => {
  const constants = createConstants(config.namespace);
  const selectors = createSelectors(config.namespace);
  const actions = createActions(constants, config, selectors);
  const reducer = createReducer(constants);

  paginationReducers[config.namespace] = reducer;
  store.injectReducer(paginationReducerName, resetOnChangeShop(combineReducers(paginationReducers)));

  return {
    actions,
    selectors,
  };
};

export default createPaginatedReduxStore;
